<!-- src/components/InquiryForm.vue -->
<script setup lang="ts">
import {computed, onUnmounted, ref, UnwrapRef, watch} from "vue";
import CustomButton from "@/components/Button/Button.vue";
import {useInquiryStore} from "@/stores/inquiryStore";
import { VueSpinner } from "vue3-spinners";
import {useFavoritesStore} from "@/stores/favorites";
import InquiryYacht from "@/components/MultipleYachtsInquiry/components/InquiryYacht.vue";
import {useRouteHelpers} from "@/composables/useRouteHelp";
import  DOMPurify from "dompurify";
import moment from 'moment';

import * as InquiryService from "@/services/inquiry.service"
import {useRouter} from "vue-router";
import Thankyou from "@/components/Thankyou/Thankyou.vue";
import BioModal from "@/pages/AboutUs/components/BioModal.vue";
import {useSearchStore} from "@/stores/searchStore";
import {useAboutStore} from "@/stores/aboutStore";
import {useYachtStore} from "@/stores/yachtStore";
import {useFormatStore} from "@/stores/formatStore";



const props = defineProps<{
  submitData: Record<string, any>;
  formData: UnwrapRef<{
    firstName: string;
    lastName: string;
    telephone: string;
    email: string;
    message: string;
    dateFrom: Date | null,
    dateTo: Date | null,
  }>;
}>();


const formRef = ref(null);

const dateFormRef = ref();

const rules = {
  dateFrom: [{ required: true, message: 'Start date is required', trigger: 'change' }],
  dateTo: [{ required: true, message: 'End date is required', trigger: 'change' }],
  dateRange: [{ required: true, message: 'Date range is required', trigger: 'change' }],
};

const sanitizeForm = (value: string) => DOMPurify.sanitize(value.trim(), {ALLOWED_TAGS: []});

const inquiryStore = useInquiryStore();
const favoritesStore = useFavoritesStore();
const yachtStore = useYachtStore();
const formatStore = useFormatStore();

const dateRangeComputed = computed({
  get() {
    return props.formData.dateFrom && props.formData.dateTo
        ? [props.formData.dateFrom, props.formData.dateTo]
        : [null, null];
  },
  set(value: [Date | null, Date | null]) {
    props.formData.dateFrom = value?.[0] ?? null;
    props.formData.dateTo = value?.[1] ?? null;
  }
});


const successMessage = ref("");
const errorMessage = ref("");
const isSubmitting = ref(false);


const { isMultiYachtsInquiryPage, isYachtPage } = useRouteHelpers();

const dateToInput = ref(null)

const router = useRouter();

const showThankYouPopup = ref(false);


function gtag_report_conversion(url, price) {
  var callback = function () {
    if (url) {
      window.location.href = url;
    }
  };
  gtag('event', 'conversion', {
    'send_to': 'AW-11327497356/WugNCKv_i4gZEIzJr5kq',
    'value': price,
    'currency': 'EUR',
    'event_callback': callback,
  });

  return false;
}


const requestHelp = async () => {
  successMessage.value = "";
  errorMessage.value = "";

  const { valid } = await formRef.value.validate();
  if (!valid) return;


  let gtagValue;
  if (isMultiYachtsInquiryPage.value) {
    gtagValue = favoritesStore.favorites.map(y => formatStore.formatNumber(formatStore.formatCurrencyNumeric(y.price)));
  } 
  else if (isYachtPage.value){
    gtagValue = formatStore.formatNumber(formatStore.formatCurrencyNumeric(yachtStore.selectedYacht.price)); 
  }
  else {
    gtagValue = null;
  }


  isSubmitting.value = true;

  try {

    // Sanitize form data before submission
    const sanitizedData = {
      ...props.submitData,
      firstName: sanitizeForm(props.formData.firstName),
      lastName: sanitizeForm(props.formData.lastName),
      telephone: sanitizeForm(props.formData.telephone),
      email: sanitizeForm(props.formData.email),
      dateFrom: moment(props.formData.dateFrom).format('DD/MM/YYYY'),
      dateTo: moment(props.formData.dateTo).format('DD/MM/YYYY'),
      message: sanitizeForm(props.formData.message),
    };



    console.log('Sanitized data:', sanitizedData);
    await InquiryService.sendHelpRequest(sanitizedData);
    // --- Google Ads conversion ---
    gtag_report_conversion(null, gtagValue)
    
    showThankYouPopup.value = true;

    successMessage.value = "Inquiry submitted successfully!";
    // Clear success message after 5 seconds
    setTimeout(() => {
      successMessage.value = "";
    }, 5000);


    inquiryStore.resetForm(props.formData);
    formRef.value.resetValidation();

    if(isMultiYachtsInquiryPage.value) favoritesStore.favorites = [];
  } catch (err: any) {
    errorMessage.value = err.message || "Problem in submitting inquiry.";
    // Clear error message after 5 seconds
    setTimeout(() => {
      errorMessage.value = "";
    }, 5000);
  } finally {
    isSubmitting.value = false;
  }
};

onUnmounted(() => {
  inquiryStore.resetForm(props.formData);
})

watch(() => props.formData.dateFrom, () => {
  if (dateToInput.value) {
    dateToInput.value.validate?.()
  }
})

watch(() => showThankYouPopup.value, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
  }
});

</script>

<template>
  <div class="relative z-10 bg-white sm:border sm:shadow-xl py-20 px-10 sm:px-25 w-full sm:max-w-6xl mx-auto">

    <div v-show="isMultiYachtsInquiryPage" class="mb-10">
      <div v-for="yacht in favoritesStore.favorites">
        <InquiryYacht :yacht="yacht"/>
      </div>
    </div>

    <v-form ref="formRef" class="grid grid-cols-1 gap-6">
      <!-- Name -->
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="w-full md:w-1/2">
          <VTextField
              v-model="props.formData.firstName"
              label="First Name"
              variant="outlined"
              class="!text-primary-darkBlue custom-border"
              :rules="[
                  v => !!v || 'Required field',
                  v => typeof v === 'string' || 'Invalid input'
              ]"
          />
        </div>
        <div class="w-full md:w-1/2">
          <VTextField
              v-model="props.formData.lastName"
              label="Last Name"
              variant="outlined"
              class="!text-primary-darkBlue"
              :rules="[
                v => !!v || 'Required field',
                v => typeof v === 'string' || 'Invalid input'
              ]"
          />
        </div>
      </div>

      <!-- Contact -->
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="w-full md:w-1/2">
          <VTextField
              v-model="props.formData.telephone"
              label="Phone Number"
              placeholder="+44 7400 123456"
              variant="outlined"
              class="!text-primary-darkBlue"
              :rules="[
              v => !!v || 'Required field',
              v => /^[0-9+\-() ]+$/.test(v) || 'Phone number must be valid'
            ]"
          />
        </div>
        <div class="w-full md:w-1/2">
          <VTextField
              v-model="props.formData.email"
              label="E-mail"
              placeholder="you@mail.com"
              type="email"
              variant="outlined"
              class="!text-primary-darkBlue"
              :rules="[
              v => !!v || 'Required field',
              v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
            ]"
          />
        </div>
      </div>


      <div class="flex flex-col sm:flex-row gap-4">

        <div class="w-full md:w-1/2">
          <v-date-input
              v-model="props.formData.dateFrom"
              label="Start date"
              variant="outlined"
              prepend-icon=""
              class="!text-primary-darkBlue"
              :min="moment().format('YYYY-MM-DD')"
              :rules="[v => !!v || 'Required field',
              ]"
          >
            <template #prepend-inner>
              <v-icon icon="mdi-calendar" class="me-2" />
            </template>
          </v-date-input>
        </div>
        <div class="w-full md:w-1/2">

          <v-date-input
              v-model="props.formData.dateTo"
              label="End date"
              ref="dateToInput"
              variant="outlined"
              prepend-icon=""
              class="!text-primary-darkBlue"
              :min="moment(props.formData.dateFrom).add(1, 'days').toDate()"
              :rules="[v => !!v || 'Required field',
              v =>
                !props.formData.dateFrom || v >= props.formData.dateFrom ||
                'End date must be after start date',
              ]"
          >
            <template #prepend-inner>
              <v-icon icon="mdi-calendar" class="me-2" />
            </template>
          </v-date-input>
        </div>

      </div>
      <!-- Message -->
      <VTextarea
          v-model="props.formData.message"
          label="Your message"
          variant="outlined"
          class="!text-primary-darkBlue"
          rows="6"
          :rules="[
              v => !!v || 'Required field',
              v => typeof v === 'string' || 'Invalid input'
          ]"
      />
    </v-form>

    <!-- Warning -->
    <div class="flex place-self-center align-center gap-2 bg-background-beige p-1 m-4 responsive-body-xxsmall sm:responsive-body-small">
      <img src="@/assets/icons/warning.png" alt="Warning" class="h-4 w-4" />
      <p class="text-primary-darkRed">
        Please include important information if applicable: Type & length of yacht, Dates, Number of guests, Budget, Destination
      </p>
    </div>

    <!-- Feedback Message -->
    <div class="text-center mt-4">
      <p v-if="successMessage" class="text-primary-green responsive-body-small font-semibold">{{ successMessage }}</p>
      <p v-if="errorMessage" class="text-red-600 responsive-body-small font-semibold">{{ errorMessage }}</p>
    </div>

    <!-- Submit -->
    <div v-if="!isSubmitting" class="flex justify-center mt-6">
      <CustomButton type="red" size="medium" class="py-4 px-6" @click="requestHelp">
        <div class="flex gap-2 items-center">
          <span>MAKE INQUIRY</span>
          <img src="@/assets/icons/navigation_arrow.svg" alt="Navigate" width="20" />
        </div>
      </CustomButton>
    </div>

    <div v-else class="grid place-content-center mt-10">
      <VueSpinner size="20" color="#beA5aa" />
    </div>

  </div>
  <Thankyou :show-thanks-modal="showThankYouPopup" @close="showThankYouPopup=false"/>

</template>

<style scoped>
:deep(.v-input__details) {
  display: none;
  padding: 5px 0 0 0 !important;
}
:deep(.v-input__details:has(.v-messages__message)) {
  display: block;
}


</style>
